import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import Button from "src/components/app/Button";
import CGMClearRestartBottomSheet from "src/components/app/bottom-sheet/cgm/CGMClearRestartBottomSheet";
import { ConnectedStep, WarmupStep } from "src/components/app/cgm/steps";

import { sendMessageToParent } from "src/helpers/common";
import storage from "src/helpers/storage";
import { cgmManager } from "src/services/cgm/cgmManager";
import { POST_MESSAGE_EVENTS, STORAGE_KEYS } from "src/constants/common";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import { warmupCompleted, warmupStarted } from "src/redux/slices/cgmSlice";
import { useCgmWebViewMessages } from "src/hooks/useCgmWebViewMessages";

const CGMStatusScreen: React.FC = () => {
  const params = useParams();
  const supportProgramKey = params["support-program-key"] ?? "";
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const cgm = useAppSelector((state) => state.cgm);
  const cgmRef = useRef(cgm);
  cgmRef.current = cgm;

  const [showClearRestartSheet, setShowClearRestartSheet] = useState(false);

  useCgmWebViewMessages();

  useEffect(() => {
    const storedWarmup = storage.getItem(STORAGE_KEYS.CGM_WARMUP_END_DATE);
    console.log(
      "[CGM status] mount — storedWarmup:",
      storedWarmup,
      storedWarmup
        ? `| remainMs: ${new Date(storedWarmup).getTime() - Date.now()}`
        : ""
    );
    if (storedWarmup) {
      const endTime = new Date(storedWarmup).getTime();
      if (endTime > Date.now()) {
        console.log("[CGM status] mount — resuming warmup from storage");
        dispatch(warmupStarted(storedWarmup));
        return;
      }
      console.log("[CGM status] mount — stored warmup expired, clearing");
      storage.removeItem(STORAGE_KEYS.CGM_WARMUP_END_DATE);
    }
    console.log("[CGM status] mount — requesting status from RN");
    cgmManager.refetchCgmStatus(undefined, cgmRef.current);
  }, [dispatch]);

  const handleWarmupComplete = useCallback(() => {
    console.log("[CGM] warmup complete — transitioning to connected");
    storage.removeItem(STORAGE_KEYS.CGM_WARMUP_END_DATE);
    dispatch(warmupCompleted());
    queueMicrotask(() => {
      sendMessageToParent(POST_MESSAGE_EVENTS.CGM_RECONNECT, {});
    });
  }, [dispatch]);

  const handleClearAndRestart = () => {
    console.log("[CGM] clear & restart — unbinding device");
    cgmManager.clearAndRestartConnection({
      navigate,
      connectionPath: `/app/${supportProgramKey}/cgm-connection`,
    });
  };

  const warmupEnd = cgm.warmupEndDate;
  const warmupTimerActive =
    !!warmupEnd && new Date(warmupEnd).getTime() > Date.now();
  const showWarmupUi =
    cgm.step === "warmup" ||
    (warmupTimerActive && cgm.displayStatus !== "EXPIRED");

  return (
    <>
      {showWarmupUi ? (
        <WarmupStep
          warmupEndDate={cgm.warmupEndDate ?? ""}
          onWarmupComplete={handleWarmupComplete}
          handleBack={() => navigate(-1)}
        />
      ) : (
        <ConnectedStep />
      )}
      <CGMClearRestartBottomSheet
        show={showClearRestartSheet}
        onCancel={() => setShowClearRestartSheet(false)}
        onConfirm={() => {
          setShowClearRestartSheet(false);
          handleClearAndRestart();
        }}
      />
    </>
  );
};

export default CGMStatusScreen;
